import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';
import { Spinner } from './Spinner.js';

export const buttonVariants = cva('btn', {
  variants: {
    variant: {
      primary: 'btn--primary',
      secondary: 'btn--secondary',
      ghost: 'btn--ghost',
      soft: 'btn--soft',
      danger: 'btn--danger',
      success: 'btn--success',
      link: 'link-quiet',
    },
    size: {
      sm: 'btn--sm',
      md: '',
      lg: 'btn--lg',
      icon: 'btn--icon',
    },
    block: { true: 'btn--block', false: '' },
  },
  defaultVariants: { variant: 'secondary', size: 'md', block: false },
});

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Rend le composant enfant (ex. un `<Link>`) en conservant le style du bouton. */
  asChild?: boolean;
  /** Affiche un indicateur d'activité et neutralise les interactions. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    block,
    asChild = false,
    loading = false,
    children,
    disabled,
    type,
    ...props
  },
  ref,
) {
  const shared = {
    ref,
    className: cn(buttonVariants({ variant, size, block }), className),
    'data-loading': loading ? 'true' : undefined,
    'aria-busy': loading || undefined,
    ...props,
  };

  // `asChild` : Radix Slot exige un enfant unique — pas d'indicateur ajouté.
  if (asChild) {
    return <Slot {...shared}>{children}</Slot>;
  }

  return (
    <button {...shared} disabled={disabled || loading} type={type ?? 'button'}>
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
});

/** Bouton icône de la barre supérieure et des lignes de tableau. */
export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string }
>(function IconButton({ className, label, children, type, ...props }, ref) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn('icon-btn', className)}
      aria-label={label}
      {...props}
    >
      {children}
    </button>
  );
});
